import { IAccessory, TAccessorySku } from '../typings';
import { IPropsProductCard } from '@/components/ProductCard/typings';
import { formatPriceThousands } from '@/utils/configs/price.config';
import { ISessionProduct } from '../typings';
import { SESSION_COMMODITY_KEY, RELATED_ACCESSORIES_FROM } from '../constants';
import { getQuery } from '@/utils/url';
import { getAdapterByUrlKey } from '@/utils/configs/adapterIds.configs';
import { isNeedHidePriceProduct } from 'server/const/product';
import { removeBrandName } from '@/utils/product';

export const hasNewTag = (accessory: TAccessorySku) => {
  return accessory.newTagInfo;
};
export const isStockout = (accessory: TAccessorySku) => {
  return accessory.commodity?.stockCount <= 0 || accessory.commodity?.saleState < 0;
};

/** 获取dom的滚动情况 */
export function getScrollPosition(dom: HTMLElement) {
  // 留下2px的阈值，以防出现滚动时出现小数点导致判断不准的情况；
  const thread = 2;
  /** 最大的滚动长度 （总长度 - 可见长度） */
  const maxScrollLength = dom.scrollWidth - dom.clientWidth;
  /** 左边滚动长度 */
  const scrollLeft = dom.scrollLeft;
  /** 是否已经滚动至最左边 */
  const isLeftEnd = scrollLeft <= thread;
  /** 是否已经滚动至最右边 */
  const isRightEnd = scrollLeft + thread >= maxScrollLength;

  return {
    /** 最大的滚动长度 （总长度 - 可见长度） */
    maxScrollLength,
    /** 左边滚动长度 */
    scrollLeft,
    /** 长度足够，无需滚动 */
    noScroll: maxScrollLength === 0,
    /** 是否已经滚动至最左边 */
    isLeftEnd,
    /** 是否已经滚动至最右边 */
    isRightEnd,
  };
}

/** 获取session storage存放的套餐id */
export function getSessionProduct() {
  try {
    const sessionProduct: ISessionProduct | null = JSON.parse(sessionStorage.getItem(SESSION_COMMODITY_KEY) ?? 'null');
    return sessionProduct;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/** 重定向 */
export function redirectToPDP() {
  try {
    const productUrlKey = new URLSearchParams(window.location.search).get('product') || '';

    // 当前是否支持的产品
    const supportedProduct = getAdapterByUrlKey(productUrlKey);

    if (!productUrlKey || !supportedProduct) {
      window.location.href = '/';
    } else {
      window.location.href = `/product/${supportedProduct.urlKey}`;
    }
  } catch (err) {
    console.error(err);
  }
}

/**
 * 格式转换器
 * 配件数据格式 -> 商品卡片数据格式
 * */
export const productDataTransformer = (c: IAccessory, countryCode: string): TAccessorySku => {
  // 逻辑拷贝至：src/pages/accessories/components/AccessoryTag/index.js
  // 有点迷，不知道为什么有两个变量控制缺货
  const isStockOut = c.commodityTradeRule?.stockCount <= 0 || c.itemInfos?.commoditySaleState < 0;
  const isOutOfStockTag = c.homeItemPrice?.showOutOfStock || (!c.homeItemPrice?.showSpuPrice && isStockOut);

  const hasNewTag = c.tagInfos?.some((o) => o.tagGroupType === 'new');

  return {
    commodity: {
      commodityId: c.commodityTradeRule?.commodityId,
      commodityName: c.itemInfos.commodityName,
      createTime: c.createTime,
      functionDescription: c.itemInfos.functionDescription || '',
      imageUrl: c.itemInfos.commodityDisplayImage,
      orderBuyLimit: c.commodityTradeRule?.orderBuyLimit,
      saleState: c.itemInfos.commoditySaleState,
      stockCount: c.commodityTradeRule?.stockCount,
    },
    product: {
      productId: c.productId,
      productName: removeBrandName(c.itemInfos.productName),
      reviewRateAvg: c.reviewRateInfo.reviewRateAvg,
      urlKey: c.itemInfos.productKey,
    },
    newTagInfo: hasNewTag,
    outOfStockTag: isOutOfStockTag,
    outOfStockButton: isStockOut,
    commodityPrice: {
      originPrice: c.homeItemPrice.originPrice,
      price: c.homeItemPrice.price,
    },
    homeItemPrice: c.homeItemPrice,
    adapterTypeNames: c.adapterTypeNames,
    isNeedHidePrice: isNeedHidePriceProduct(c.productId, countryCode),
  };
};

/**
 * 配件页的商品价格格式器
 * 逻辑照搬：src/pages/home/utils/index.jsx
 */
export const priceFormatter: IPropsProductCard<TAccessorySku>['priceFormatter'] = (
  sku: TAccessorySku,
  country: TBasicCountryInfo
) => {
  const priceInfo = sku.homeItemPrice;

  // 显示价格
  const showPrice = priceInfo.price
    ? formatPriceThousands({
        price: priceInfo?.price?.amount,
        country,
        signal: priceInfo?.price?.signal,
        currency: priceInfo?.price?.currency,
      })
    : '';

  // 最低价
  const fromPrice = priceInfo.showSpuPrice ? showPrice : '';

  // 划线价（原价）
  const listPrice = priceInfo?.originPrice
    ? formatPriceThousands({
        price: priceInfo?.originPrice?.amount,
        country,
        signal: priceInfo?.originPrice?.signal,
        currency: priceInfo?.originPrice?.currency,
        showCurrency: false,
      })
    : '';

  // 节省价格
  const savedPrice = priceInfo?.maxDiscount
    ? formatPriceThousands({
        price: priceInfo?.maxDiscount.amount,
        country,
        signal: priceInfo?.maxDiscount.signal,
        currency: priceInfo?.maxDiscount.currency,
      })
    : '';

  return {
    listPrice,
    showPrice,
    fromPrice,
    savedPrice,
    isSaveUpTo: !!savedPrice && priceInfo.showSpuPrice,
  };
};

/** 暂存套餐信息 */
export function stashCidInSessionStorage(value: ISessionProduct) {
  try {
    window.sessionStorage.setItem(SESSION_COMMODITY_KEY, JSON.stringify(value));
  } catch (err) {
    console.error(err);
  }
}

/** 判断跳转来源是否为推荐配件页（/related-accessories） */
export function isComeFromRelatedAccessoriesPage() {
  return getQuery('from') === RELATED_ACCESSORIES_FROM;
}
